import type { UserManager } from '@skyslope/auth-js';

import { userManager } from 'auth/userManager';
import { ACCOUNT_API_URL, FILE_API_URL } from 'constants/environments';
import type { Memberships, StripeSubscription, Trial } from 'resources/types/GetUserSubscription';
import type { SendToStripeRequest } from 'resources/types/SendToStripeRequest';
import type { AccountGroup, AccountPermission, GroupPermission, Roles } from 'store/types';
import { expandPermission } from 'utils/accountPermissionUtils';

import Server from './Server';

// Account API Response model (uncomment additional properties if needed)
interface UserGroup {
  _id: string;
  name: string;
  userCount: number;
  // parentGroupId: string;
  // isInternal: boolean;
  // createdAt: string;
  // updatedAt: string;
  // createdBy: string;
  // updatedBy: string;
  // source: string;
  // location: string;
  // metadata: string;
  // isDeleted: boolean;
}

class AccountUserServer extends Server {
  public constructor(manager: UserManager) {
    super(manager);
  }

  public async GetRolebyUserId(userId: string): Promise<Roles> {
    return (await this.api.get(`${ACCOUNT_API_URL}/v3/user/id?Id=${userId}`)).data.result;
  }

  public async GetGroupsWithPermissionByUserId(permission: AccountPermission, userId: string): Promise<AccountGroup[]> {
    const results = (await this.api.get(`${ACCOUNT_API_URL}/v3/user/id?Id=${userId}&groupOption.loadAll=true`)).data
      .result;

    // First, filter out any that don't that have the permission
    const filteredGroupPermissions: GroupPermission[] = results.userPermissions.filter((p: GroupPermission) => {
      const expandedPermissions = expandPermission(p.accountPermission);
      return expandedPermissions.includes(permission);
    });

    // Second, map the full group details in (adding properties as needed)
    const groupsWithPermission = filteredGroupPermissions.map((g) => {
      const matchingGroup: UserGroup = results.userGroups.find((ug: UserGroup) => ug._id === g.groupId);
      return {
        groupId: g.groupId,
        name: matchingGroup?.name,
        userCount: matchingGroup?.userCount,
      };
    });

    return groupsWithPermission;
  }

  public async GetSubscriberGroup(subscriberId: string): Promise<AccountGroup[]> {
    const results = (
      await this.api.post(`${ACCOUNT_API_URL}/v3/groups/filter`, {
        GroupFilters: [
          {
            MetadataFilters: [{ SubscriberId: subscriberId }],
          },
        ],
      })
    ).data.result;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return results.map((group: any) => ({
      groupId: group._id,
      name: group.name,
      userCount: group.userCount,
    }));
  }

  public async GetGroups(page: number, pageSize: number): Promise<AccountGroup[]> {
    return (await this.api.get(`${FILE_API_URL}/groups?page=${page ?? 1}&pageSize=${pageSize ?? 2000}`))?.data?.result
      ?.data;
  }

  public async getUsersInGroup(
    groupId?: string
  ): Promise<Array<{ email: string; firstName: string; lastName: string }>> {
    if (groupId == undefined) return Promise.resolve([]);

    return (await this.api.get(`${FILE_API_URL}/groups/${groupId}/users`))?.data?.result;
  }

  public async getStripeSubscription(oktaId?: string): Promise<StripeSubscription> {
    const response = await this.api.get(`${ACCOUNT_API_URL}/v3/users/${oktaId}/subscription`);
    return response.data;
  }

  public async getMemberships(): Promise<Memberships> {
    const response = await this.api.get(`${ACCOUNT_API_URL}/v3/users/me/subscription`);
    return response.data?.result;
  }

  public async sendToStripe(args: SendToStripeRequest): Promise<string> {
    const { priceId, redirectUrl, oktaGuidId } = args;
    const response = await this.api.post(`${ACCOUNT_API_URL}/v3/users/subscription/checkout`, {
      priceId,
      redirectUrl,
      userId: oktaGuidId,
    });
    return response.data.url;
  }

  public async updateUserSubscription(id: string): Promise<StripeSubscription> {
    const res = await this.api.post(`${ACCOUNT_API_URL}/v3/users/${id}/subscription/`);
    return res.data;
  }

  public async getTrial(): Promise<Trial> {
    const { data } = await this.api.get(`${ACCOUNT_API_URL}/v3/users/me/trial`);
    return data.result;
  }

  public async createTrial(id?: string) {
    const { data } = await this.api.post(`${ACCOUNT_API_URL}/v3/users/${id}/trial`, {});
    return data.result;
  }
}

export default new AccountUserServer(userManager);
